import React, { useMemo } from 'react'
import {
  Drawer,
  DrawerBody,
  Box,
  DrawerContent,
  DrawerOverlay,
  useDisclosure,
  Flex,
  Avatar,
  Text,
  List,
  DrawerCloseButton,
  ListItem,
  HStack,
  theme,
  useColorModeValue,
  IconButton,
  useColorMode
} from '@chakra-ui/react'
import { VscClose, VscHome, VscMenu, VscQuestion } from 'react-icons/vsc'

import { useAuthContext } from 'context/AuthProvider'
import { useHistory } from 'react-router'

import { CustomLink, LangSelector } from 'components'
import { get } from 'js-cookie'

import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { SliderButton } from '@typeform/embed-react'
import { LANGUAGES, CONTACT_SUPPORT_FORM_ID } from '../../constants'
import { FiSun, FiMoon } from 'react-icons/fi'

type SideBarProps = {}

const SideBar: React.FC<SideBarProps> = ({}) => {
  const { isOpen, onClose, onToggle } = useDisclosure()
  const { user, isAuthenticated } = useAuthContext()
  const { toggleColorMode, colorMode } = useColorMode()
  const history = useHistory()
  const { t } = useTranslation()
  const menuItems = useMemo(
    () => [
      {
        title: t('top_nav_home_link'),
        Icon: VscHome,
        to: isAuthenticated ? '/auth' : '/'
      },
      {
        title: t('top_nav_about_us_link'),
        Icon: VscQuestion,
        to: isAuthenticated ? '/auth/about-us' : '/about-us'
      }
    ],
    [isAuthenticated, t]
  )
  const color = useColorModeValue(theme.colors.orange[400], theme.colors.orange[200])
  const bg = useColorModeValue('white', theme.colors.gray[800])

  const { icon } = LANGUAGES.find((l) => l.code === get('lng')) || { code: 'en' }

  return (
    <>
      <Box as={isOpen ? VscClose : VscMenu} size={24} aria-label="menu" ml={2} onClick={onToggle} />

      <Drawer onClose={onClose} isOpen={isOpen} size="full">
        <DrawerOverlay />
        <DrawerContent bg={bg} zIndex={48}>
          <DrawerCloseButton my={3} />
          <Box px={6} py={4} mt={8}>
            {isAuthenticated ? (
              <Flex
                align="center"
                mt={4}
                onClick={() => {
                  history.push('/auth/profile')
                  onClose()
                }}
              >
                <Avatar size="md" name={user?.username} />
                <Flex mx={5} direction="column">
                  <Text fontWeight={'semibold'} fontSize="lg">
                    {user?.username}
                  </Text>
                  <Text fontWeight="light" fontSize="sm">
                    {user?.email}
                  </Text>
                </Flex>
              </Flex>
            ) : (
              <HStack justifyContent="space-between" mt={4}>
                <CustomLink path="/login" fontWeight="bold" title={t('sign_in')} active={false} />
                <Box>
                  <IconButton
                    icon={colorMode === 'dark' ? <FiSun size={10} /> : <FiMoon size={10} />}
                    aria-label="Toggle color mode"
                    rounded="full"
                    _hover={{
                      bg: 'transparent'
                    }}
                    onClick={toggleColorMode}
                    colorScheme="gray"
                  />
                  <LangSelector m={0}>
                    <Text fontSize="xs">
                      {icon} {get('lng')}
                    </Text>
                  </LangSelector>
                </Box>
              </HStack>
            )}
          </Box>

          <DrawerBody py={4}>
            <List spacing={6} fontSize="xl" minH="84vh">
              {menuItems.map(({ title, to }, idx) => (
                <ListItem key={idx} cursor="pointer">
                  <NavLink
                    activeStyle={{
                      fontWeight: 'bold',
                      color
                    }}
                    exact
                    to={to}
                  >
                    <Flex align="center">
                      {/* <ListIcon size={iconSizes.md} as={Icon} /> */}
                      <Text fontWeight="semibold" fontSize="lg">
                        {title}
                      </Text>
                    </Flex>
                  </NavLink>
                </ListItem>
              ))}
              <ListItem cursor="pointer">
                <SliderButton id={CONTACT_SUPPORT_FORM_ID}>
                  <Text position="relative" cursor="pointer">
                    Contact Support
                  </Text>
                </SliderButton>
              </ListItem>
            </List>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}

export default SideBar
